import { useEffect, useState } from "react";

// HOOKS
import { usePayment } from "src/hooks/context";
import { useGetAgreementFromForeignId } from "../../hooks/useGetAgreementFromForeignId";
import { useTriloCallback } from "../../hooks/useTriloCallback";

// COMPONENTS
import CamelBottom from "src/components/Layout/CamelBottom/CamelBottom";
import CamelTop from "src/components/Layout/CamelTop/CamelTop";

// ASSETS
import { PaymentSummary } from "./PaymentSummary";
import { Receipt } from "./Receipt";

type CallbackProps = {
    goToPaymentSummary: (foreignId: string) => void;
}

export const Callback = ({ goToPaymentSummary }: CallbackProps) => {
    const { paymentState, updatePaymentState } = usePayment();
    const [paymentStatuses, setPaymentStatuses] = useState<{
        isSettled?: boolean;
        isAccepted?: boolean;
        isError?: boolean;
    }>({});
    const [agreementId, setAgreementId] = useState<string | null>(null);
    const { triloCallback } = useTriloCallback();
    const { agreementFromForeignId } = useGetAgreementFromForeignId();
    const [transferId, setTransferId] = useState<string | null>(null);
    const [provider, setProvider] = useState<string | null>(null);
    const [isPayment, setIsPayment] = useState<boolean | null>(null);

    // Example Callback URL:
    // https://rohan-pay.trilo.io/callback/t/giraffe#code=02d88919-b708-4eda-a23a-d0d883c3447a&state=eyJiYW5rU2Vzc2lvbklkIjoiMDJkODg5MTktYjcwOC00ZWRhLWEyM2EtZDBkODgzYzM0NDdhIiwiYW1vdW50IjoiMS4wMCIsImN1cnJlbmN5IjoiR0JQIiwidHJhbnNmZXJJZCI6IjBmYmE2ZTQyLWE2NmEtNGJjMi04NTBiLWMzZWRiOTQ1MGIxMyJ9
    // https://p.trilo.io/callback/y?code=oa_prod_sgS006nv4pDLBBzHUEhL9biVoY3AenoTCgCk3XydRkY&id_token=eyJraWQiOiJZa2ViNmQ1d1Uwa2I5TU0zcHNGYm82YjNDVXciLCJhbGciOiJQUzI1NiJ9.eyJpc3MiOiJodHRwczovL29iYS5yZXZvbHV0LmNvbSIsImlhdCI6MTczNjI1MzUyNCwic3ViIjoiM2MwOTU4NmQtMTk5ZC00M2JjLTgxYTMtMzdlY2NiZTUwZjI5IiwiYWNyIjoidXJuOm9wZW5iYW5raW5nOnBzZDI6c2NhIiwib3BlbmJhbmtpbmdfaW50ZW50X2lkIjoiM2MwOTU4NmQtMTk5ZC00M2JjLTgxYTMtMzdlY2NiZTUwZjI5IiwiYXVkIjoiMjJjZTk3MWMtMTZjNC00MzFhLTk0YmYtM2ZlOTdiOTIyZmNhIiwibm9uY2UiOiJwZXAxYTAzZThlOWY0NDlmODFjZmZjNTYyMmNiYzg2YyIsImV4cCI6MTczNjI1NDEyNCwiY19oYXNoIjoiendweXl0M3ViTS1memVrN09NN1pQQSIsInNfaGFzaCI6IndyamQ5ZmFJRnYxQWU1bk5USjBwUlEifQ.NDeak5YLGWWXpaQ7v2i7sZc2c9W9W8fCOM8TPGTWW0XOwyKfWw8Jec9L3CJ34VJOuEADTwD8DBGGYZuNG90OHEEYiCgcmbNDUoTW_gwaekGhfQ2pMwg2dCsOBwjIXWIlluxC85pM2Tl1Zjb4S4J4713iiIqTmAq9tFNlwwsrbjyXH27U22ChW2FGOyGvlNVAyVXjMdlF7HdYcjQuh5F_9seQeXqgP0rg7lOPtz1Ig00TC3WIqYZxHLl3rspwKP3Eci3DJPggXZ-e0kyq40RgQM_pRGDubyrj52pQx756KdpVNGWs-KHJPD3mEG0N-AbKaD37hquEJhdFD0sGY59wsg&state=pep1a03e8e9f449f81cffc5622cbc86c
    const extractPathnameValues = ({ pathname }: { pathname: string }) => {
        let params;

        // Check if the pathname contains a hash
        if (pathname.includes('#')) {
            // Remove the leading '#' to parse the query string
            const queryString = pathname.split('#')[1];
            params = new URLSearchParams(queryString);
        } else {
            // If there's no hash, treat the pathname as a regular query string
            const queryString = pathname.split('?')[1];
            params = new URLSearchParams(queryString);
        }

        // Extract the values
        const code = params.get('code');
        const state = params.get('state');

        return { code, state };
    }

    const extractUrl = () => {
        return window.location.href;
    }

    const extractProviderFromPath = () => {
        const pathname = window.location.pathname;

        if (pathname.includes('/callback/y')) {
            return 'yapily';
        } else if (pathname.includes('/callback/t')) {
            return 'trilo';
        }

        return null;
    }

    useEffect(() => {
        // Extract and set provider from URL path
        const extractedProvider = extractProviderFromPath();
        setProvider(extractedProvider);
        console.log("[Callback] provider", { extractedProvider });

        const { state: foreignId } = extractPathnameValues({ pathname: window.location.href });
        if (foreignId === null || foreignId === undefined) {
            console.error("foreign id is null or undefined");
            return
        }
        if (paymentState.amount !== null && paymentState.amount !== undefined) {
            return
        }
        async function getTransferFn() {
            if (foreignId === null || foreignId === undefined) {
                console.error("foreign id is null or undefined");
                return
            }

            let agreementId = null;
            let transfer = null;
            try {
                ({ id: agreementId, transfer } = await agreementFromForeignId({ foreignId }));
                setIsPayment(true);
            } catch (error) {
                console.log("Not a Payment")
                setIsPayment(false);
                return
            }

            if (transfer === null || transfer === undefined) {
                console.error("transfer is null or undefined");
                return
            }
            console.log("[Callback]", { transfer });
            updatePaymentState({
                transferId: transfer.id,
                amount: transfer.amount,
                note: transfer.note,
                status: transfer.isSettled ? "PAID" : "PENDING",
                recipient: {
                    id: "",
                    name: transfer.details?.name !== null ? transfer.details?.name : undefined,
                    logo: transfer.details?.avatar !== null ? transfer.details?.avatar : undefined,
                }
            });

            const {
                isAccepted,
                isSettled,
                isError,
            } = transfer;
            setPaymentStatuses({
                isAccepted: isAccepted ?? undefined,
                isSettled: isSettled ?? undefined,
                isError: isError ?? undefined,
            })

            if (agreementId === null || agreementId === undefined) {
                console.error("agreementId is null or undefined");
                return
            }
            setAgreementId(agreementId);
        }
        getTransferFn();

    }, [
        goToPaymentSummary,
        triloCallback,
        setTransferId,
        transferId,
        agreementFromForeignId,
        updatePaymentState,
        paymentState.amount,
        setIsPayment,
        setPaymentStatuses
    ]);


    const isPaymentPending = ({
        isAccepted,
        isSettled,
        isError,
    }: {
        isSettled?: boolean;
        isAccepted?: boolean;
        isError?: boolean;
    }) => {
        if (
            !isAccepted &&
            !isSettled &&
            !isError
        ) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        async function callbackFn() {
            const { state: foreignId } = extractPathnameValues({ pathname: window.location.href });
            if (foreignId === null || foreignId === undefined) {
                console.error("foreign id is null or undefined");
                return
            }

            if (isPayment === false) {
                const { gotoUrl } = await triloCallback(extractUrl());
                console.log("[callback] gotoUrl", gotoUrl);
                if (gotoUrl) {
                    window.location.href = gotoUrl;
                }
                return;
            }

            if (isPayment === true) {
                if (paymentState.transferId === null || paymentState.transferId === undefined) {
                    return
                }

                if (
                    paymentStatuses.isSettled === undefined ||
                    paymentStatuses.isError === undefined
                ) {
                    console.error("payment statuses have not been set yet");
                    return
                }
                if (!isPaymentPending(paymentStatuses)) {
                    console.error("payment is NOT pending; this should not have been invoked");
                    goToPaymentSummary(foreignId);
                    return
                }
                const { transferId: transferIdFromCallback } = await triloCallback(extractUrl(), paymentState.transferId || undefined);
                if (transferIdFromCallback === null || transferIdFromCallback === undefined) {
                    console.error("transferIdFromCallback is null or undefined");
                    return
                }

                goToPaymentSummary(foreignId);
            }
        }

        if (isPayment !== null) {
            callbackFn();
        }
    }, [isPayment, paymentState.transferId, agreementId, goToPaymentSummary, paymentStatuses, triloCallback]);

    return <>
        <CamelTop>
            <PaymentSummary />
        </CamelTop>
        <CamelBottom>
            {agreementId !== null && <Receipt agreementId={agreementId} />}
        </CamelBottom>
    </>;
};
